import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Banner from '../components/banner'
import useAPI from '../hooks/use-api'

// src/assets/data/purchase.json说明：
// 1. key: 产品key，用于在代码中做唯一键
// 2. productKeys: 用于从product页跳转到purchase页，记录对应关系：哪些product需要显示某个purchase详情，多对一
// 3. 其他配置参照字面意思即可

const Purchase = () => {
  const api = useAPI()
  const [searchParams] = useSearchParams()

  const [isLoading, setIsLoading] = useState(true)
  const [products, setProducts] = useState([])
  const [activedProduct, setActivedProduct] = useState('')
  const cardDetailRef = useRef(null)

  useEffect(() => {
    const fetchData = async () => {
      const purchaseJSON = await api.getPurchase()
      setProducts(Array.isArray(purchaseJSON) ? purchaseJSON : [])
      setIsLoading(false)
    }

    fetchData()
    // Intentionally run once on mount (useAPI returns a new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeFromUrl = useMemo(() => {
    return String(searchParams.get('active') || '').trim()
  }, [searchParams])

  useEffect(() => {
    if (!activeFromUrl) return
    if (!Array.isArray(products) || products.length === 0) return

    const matched = products.find(p => {
      const key = String(p?.key || '')
      if (!key) return false
      if (key === activeFromUrl) return true

      const productKeys = Array.isArray(p?.productKeys) ? p.productKeys : []
      return productKeys.includes(activeFromUrl)
    })

    const nextKey = String(matched?.key || '')
    if (!nextKey) return
    setActivedProduct(nextKey)
  }, [activeFromUrl, products])

  const handleToggleProduct = productKey => event => {
    if (!productKey) return
    event?.currentTarget?.blur?.()
    setActivedProduct(prev => (prev === productKey ? '' : productKey))
  }

  const activeProduct = useMemo(() => {
    return products.find(p => p?.key === activedProduct) || null
  }, [activedProduct, products])

  // 滚动到选中的Card Detail顶部
  useEffect(() => {
    if (!activeProduct) return

    const el = cardDetailRef.current
    if (!el) return

    const rafId = window.requestAnimationFrame(() => {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } catch (err) {
        el.scrollIntoView(true)
      }
    })

    return () => {
      window.cancelAnimationFrame(rafId)
    }
  }, [activeProduct])

  if (isLoading) {
    return <h1 className="text-2xl p-5 text-center">Loading...</h1>
  }

  return (
    <div>
      <div className="hidden md:block w-full banner-placeholder">
        {/* 设置为banner图片的宽高，方便滚动页面到product details时准确定位 */}
        <Banner
          className="pointer-events-none"
          src={require('@/assets/img/purchase/pc_banner.jpg').default}
          width={3360}
          height={960}
        />
      </div>

      <div className="md:hidden w-full banner-placeholder">
        {/* 设置为banner图片的宽高，方便滚动页面到product details时准确定位 */}
        <Banner
          className="object-fit pointer-events-none"
          src={require('@/assets/img/purchase/mobile_banner.jpg').default}
          width={375}
          height={593}
        />
      </div>

      <div className="mx-auto my-10 max-w-[1260px] px-2">
        <h1 className="text-center mb-8">วิธีการซื้อประกัน</h1>

        <div className="mx-auto flex max-w-[541px] flex-wrap items-stretch justify-center gap-x-2 gap-y-4 md:max-w-[798px] md:gap-x-6 md:gap-y-6 font-shopee">
          {products.map(product => {
            const productKey = String(product?.key || '')
            const isActive = activedProduct === productKey

            const logoSrc = product?.logo ? require(`@/${String(product.logo).replace(/^src\//, '')}`).default : null

            const buttonClassName = [
              'noSelect w-full rounded-lg border px-2 md:px-3 py-2 text-[12px] leading-[1.2] md:text-sm md:leading-[1.2]',
              'transition-colors flex items-center justify-center h-[4.4em] md:h-auto',
              // Avoid sticky :hover on touch devices; only enable hover on md+.
              !isActive && 'md:hover:text-[#004eeb] md:hover:border-[#004eeb]',
              !isActive && 'active:text-[#004eeb] active:border-[#004eeb]',
              isActive ? 'text-[#004eeb] border-[#004eeb]' : 'text-[#6b7280] border-[#d1d5db]',
            ].join(' ')

            return (
              <div
                key={productKey}
                className="flex flex-none h-full w-[175px] md:w-[250px] flex-col items-center rounded-2xl border border-[#e5e7eb] bg-white px-2 md:px-4 pt-5 pb-4 shadow-sm"
              >
                <div className="h-16 md:h-20 w-full flex items-center justify-center">
                  {logoSrc ? (
                    <img
                      alt={`${product?.title || productKey}-logo`}
                      className="max-h-16 md:max-h-20 w-auto object-contain pointer-events-none"
                      loading="lazy"
                      src={logoSrc}
                    />
                  ) : (
                    <div className="h-12 md:h-14 w-full" />
                  )}
                </div>

                <div className="mt-3 w-full text-center">
                  <div className="text-base md:text-lg font-semibold leading-[1.2] md:leading-[1.2] text-[#111827] h-[2.4em] overflow-hidden break-words text-center">
                    {product?.title}
                  </div>
                  <div className="mt-2 text-[12px] leading-[1.2] md:text-sm md:leading-[1.2] text-[#6b7280] h-[5.4em] md:h-auto">
                    {product?.description}
                  </div>
                </div>

                <div className="mt-auto w-full pt-3">
                  <button type="button" className={buttonClassName} onClick={handleToggleProduct(productKey)}>
                    {product?.btnText}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {!!activeProduct && (
          <div ref={cardDetailRef} className="mt-8 md:mt-10 w-full pt-4 pb-4 md:pt-6 md:pb-6 bg-white font-shopee">
            <div className="flex flex-col gap-10 md:gap-20">
              {!!activeProduct?.qrCodes?.length && (
                <div className="flex md:flex-row flex-col items-stretch justify-center gap-6 md:gap-10">
                  {activeProduct.qrCodes.map((qr, index) => {
                    const productKey = String(activeProduct?.key || activedProduct || '')
                    const qrKey = `${productKey}-qr-${index}`
                    const qrImgSrc = qr?.img ? require(`@/${String(qr.img).replace(/^src\//, '')}`).default : null

                    return (
                      <div key={qrKey} className="flex flex-col items-center justify-start gap-3">
                        <div className="text-center text-base md:text-lg text-[#111827] whitespace-pre-line">
                          {qr?.text}
                        </div>
                        {qrImgSrc && (
                          <img
                            alt={`${productKey}-qrcode-${index}`}
                            className="h-[120px] w-[120px] object-contain pointer-events-none"
                            loading="lazy"
                            src={qrImgSrc}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {!!activeProduct?.flows?.length && (
                <div className="flex flex-col gap-6 md:gap-10">
                  {activeProduct.flows.map((flow, index) => {
                    const productKey = String(activeProduct?.key || activedProduct || '')
                    const flowKey = `${productKey}-flow-${index}`
                    const flowImgSrc = flow?.img ? require(`@/${String(flow.img).replace(/^src\//, '')}`).default : null

                    return (
                      <div key={flowKey} className="flex flex-col items-center">
                        <div className="text-center text-[#004eeb] font-semibold text-base md:text-lg">
                          {flow?.text}
                        </div>
                        {flowImgSrc && (
                          // 设置min-height，方便滚动到Card Detail顶部、且图片尚未加载时，撑开页面内容，一遍能滚动到正确的位置
                          <img
                            alt={`${productKey}-flow-${index}`}
                            className="w-full max-w-[920px] rounded-xl object-contain pointer-events-none md:min-h-[400px]"
                            loading="lazy"
                            src={flowImgSrc}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Purchase
